<template>
    <div class="border-none">
        <div v-if="isTrialing" class="p-3 mb-4 text-center bg-green-500 rounded-lg">
            <p class="text-sm italic font-medium leading-6 text-white">
                You are on a free trial for {{ trialDays }} more days.
            </p>
        </div>
        <form method="POST" :action="paymentRoute" id="payment-form">
            <input type="hidden" name="token" id="token" :value="token">
            <input type="hidden" name="stripe_id" :value="getStripeId"/>
            <div class="bg-white shadow sm:rounded-md sm:overflow-hidden">
                <div class="px-4 py-6 sm:p-6">
                    <section aria-labelledby="plan-heading">
                        <div class="flex justify-center w-full">
                            <div class="flex items-center w-full">
                                <fieldset class="grid grid-cols-2 p-1 text-xs font-semibold leading-5 text-center rounded-lg gap-x-1 ring-1 ring-inset ring-gray-200 w-full">
                                    <legend class="sr-only">Payment frequency</legend>
                                    <label class="cursor-pointer rounded-lg px-2.5 py-3"
                                        :class="[currentPlans === 'monthly' ? 'bg-blue-600 text-white' : '']"
                                    >
                                        <input type="radio" @change="toggleCurrentPlan" name="frequency" value="monthly" class="sr-only">
                                        <span class="text-md font-black">Monthly</span>
                                    </label>
                                    <label class="cursor-pointer rounded-lg px-2.5 py-3"
                                        :class="[currentPlans === 'yearly' ? 'bg-blue-600 text-white' : '']"
                                    >
                                        <input type="radio" @change="toggleCurrentPlan" name="frequency" value="annually" class="sr-only">
                                        <span class="text-md font-black">Annually (2 Months Free)</span>
                                    </label>
                                </fieldset>
                            </div>
                        </div>
                        <fieldset>
                            <div class="grid grid-cols-1 mt-4 gap-y-6 sm:grid-cols-3 sm:gap-x-6">
                                <label
                                    class="flex items-start p-4 bg-white border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier3.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier3.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex flex-1">
                                        <span class="flex flex-col w-full">
                                            <span class="flex justify-between w-full font-medium text-gray-900 text-md">
                                                <p>Enterprise</p>
                                                <svg v-if="currentTier3.identifier === plan.identifier" class="w-5 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-gray-500">Best for industry executives looking for a full picture of the industry</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-red-600 line-through text-md">$399.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-red-600 line-through text-md">$4,788.00 /year</span>
                                            <span class="mt-2 font-bold text-gray-900 text-md">{{ showPlanPrice(currentTier3.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Sales Channel Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Manufacturer Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Health Network Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Team Collaboration
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                                <label
                                    class="flex items-start p-4 bg-blue-600 border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier2.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier2.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex object-top">
                                        <span class="flex flex-col w-full">
                                            <span class="flex justify-between w-full font-medium text-white text-md">
                                                <p>Premium</p>
                                                <svg v-if="currentTier2.identifier === plan.identifier" class="w-5 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-white">Best for sales managers looking to expand their distribution network</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-white line-through text-md">199.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-white line-through text-md">$2,388.00 /year</span>
                                            <span class="mt-2 font-bold text-white text-md">{{ showPlanPrice(currentTier2.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Sales Channel Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Manufacturer Profiles
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                                <label
                                    class="flex items-start p-4 bg-white border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier1.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier1.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex ">
                                        <span class="flex flex-col object-top w-full">
                                            <span class="flex justify-between w-full font-medium text-gray-900 text-md">
                                                <p>Basic</p>
                                                <svg v-if="currentTier1.identifier === plan.identifier" class="w-5 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-gray-500">Best for new reps looking to supercharge their pipeline</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-red-600 line-through text-md">$99.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-red-600 line-through text-md">$1,188.00 /year</span>
                                            <span class="mt-2 text-lg font-bold text-gray-900">{{ showPlanPrice(currentTier1.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                            </div>
                        </fieldset>

                    </section>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-between w-full">
                            <div class="text-gray-500">
                                <span class="text-sm">Plan Price billed {{ currentPlans }}:</span>
                            </div>
                            <div class="text-gray-500">
                                <span class="text-md">{{ currentPlanPrice() }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-end w-full">
                            <div class="text-gray-500 ">
                                <span class="text-md">x  <select id="plan-quantity" name="quantity" @change="changePlanSize($event)"
                                        class="max-w-full py-1 text-base font-medium leading-5 text-left text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                        <option
                                            v-for="i in 25"
                                            :key = i
                                            :value="i"  :disabled="lessThanMinimum(i)"
                                            :selected="i === quantity">{{ i }}
                                        </option>

                                    </select> seat(s)</span>
                            </div>
                        </div>
                    </div>
                    <div class="py-4 mt-4 border-b-2 md:flex md:justify-between md:items-end md:space-x-4">
                        <div>
                            <label for="promo-code" class="block text-sm font-medium text-gray-500">Promo Code</label>
                            <span class="flex items-center justify-start space-x-2">
                                <input
                                    v-model="userPromoCode"
                                    type="text"
                                    name="promo_code"
                                    id="promo-code"
                                    class="py-1 md:w-60 tw-input"
                                >
                                <a
                                    href="#"
                                    class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    @click="checkPromoCode"
                                >Apply</a>
                            </span>
                            <div class="mt-2 text-sm text-red-600" v-if="error !== '' ">{{ error }}</div>
                        </div>
                        <div v-if="totalDiscount > 0">
                            <span class="text-sm text-gray-500">
                                {{discount.code}} <span class="font-medium text-green-600"> - {{ moneyToString(totalDiscount/100) }}</span>
                            </span>
                        </div>
                    </div>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-between w-full">
                            <div class="font-semibold text-gray-900 text-md">
                                Total:
                            </div>
                            <div class="font-medium text-gray-900 text-md">
                                {{ moneyToString(total/100) }}
                                <span class="text-sm text-gray-500">billed {{ currentPlans }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="p-4 bg-white">
                    <section aria-labelledby="payment-heading">
                        <div class="w-full mb-4 md:grid md:grid-cols-4 md:gap-6">
                            <div class="md:col-span-2">
                                <label for="card-holder-name" class="block text-sm font-medium text-gray-700">Name on card</label>
                                <input type="text" name="name" :value="user.name" id="card-holder-name"
                                class="tw-input">
                            </div>
                        </div>

                        <div class="mt-6 md:grid md:grid-cols-4 md:gap-6">
                            <div class="md:col-span-4 lg:col-span-3">
                                <div id="card-element" class="tw-input-parent"></div>
                            </div>
                            <div class="col-span-1">
                                <button type="submit" :data-secret="intent.client_secret" id="card-button"
                                    class="inline-flex items-center tw-button-green">
                                    <svg id="card-button-icon" class="hidden w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                                        viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                    Register
                                </button>
                            </div>
                            <div class="hidden mt-2 text-sm text-red-600" id="card-errors" role="alert"></div>
                        </div>
                    </section>

                </div>
            </div>
        </form>
    </div>
</template>
<script>

export default {
    props: {
        tierThreePlans: {
            type: [Object, Array],
            required: true
        },
        tierTwoPlans: {
            type: [Object, Array],
            required: true
        },
        tierOnePlans: {
            type: [Object, Array],
            required: true
        },
        user: {
            type: [Object, Array],
            required: true,
        },
        promoCodes: {
            type: [Object, Array],
            required: false,
        },
        intent: {
            type: [Object, Array],
            required: true,
        },
        defaultTrialDays: {
            type: Number,
            required: false,
        },
        paymentRoute: {
            type: String,
            required: true,
        },
        subscribed: {
            type: Boolean,
            required: false
        },
        subscription: {
            type: [Object, Array],
            required: false,
        },
        token: {
            type: String,
            required: true,
        },
        isTrialing: {
            type: Boolean,
            required: false,
        },
        trialDays: {
            type: [String, Number],
            required: false,
        },
        registrationPromoCode: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            selectedPlanIndex: 0,
            currentPlans: 'yearly',
            total: this.tierTwoPlans['yearly'].price,
            billingInterval: 'year',
            userPromoCode: '',
            discount: {
                code: '',
                duration: '',
                duration_in_months: '',
                percent_off: 0,
                amount_off: 0,
            },
            totalDiscount: 0,
            error: '',
            plan: this.tierTwoPlans['yearly'],
            plan_identifier: this.tierTwoPlans['yearly'].identifier,
            quantity: 1,
        }
    },
    created() {
        this.getRegistrationPromoCode();
        this.checkPromoCode();
    },
    watch: {
        userPromoCode( code ) {
            if ( !code ) {
                this.totalDiscount = 0;
                this.total = this.plan.price;
                this.discount = {
                    code: '',
                    duration: '',
                    duration_in_months: '',
                    percent_off: 0,
                    amount_off: 0,
                };
            }
        },
    },
    computed: {
        getStripeId() {
            return this.plan.stripe_price;
        },
        currentTier1() {
            return this.currentPlans === 'yearly' ?  this.tierOnePlans['yearly'] : this.tierOnePlans['monthly'];
        },
        currentTier2() {
            return this.currentPlans === 'yearly' ? this.tierTwoPlans['yearly'] : this.tierTwoPlans['monthly'];
        },
        currentTier3() {
            return this.currentPlans === 'yearly' ? this.tierThreePlans['yearly'] : this.tierThreePlans['monthly'];
        },
    },
    methods: {
        calculateTotal() {
            if ( !this.discount.code ) {
                this.total = this.plan.price * this.quantity;
                return;
            }

            let discount = 0;

            if ( this.discount.percent_off > 0 ) {
                discount = this.plan.price * this.quantity * (this.discount.percent_off / 100);
                this.total = this.plan.price - discount;
                this.totalDiscount = discount;
                return;
            }

            if ( this.discount.amount_off > 0 ) {
                discount = this.discount.amount_off * this.quantity;

                if ( this.billingInterval == 'year' && this.discount.duration == 'repeating' ) {
                    discount = discount * this.discount.duration_in_months;
                }
                this.total = (this.plan.price * this.quantity) - discount;
                this.totalDiscount = discount;
                return;
            }
        },
        getRegistrationPromoCode() {
            if ( this.registrationPromoCode != '' ) {
                this.userPromoCode = this.registrationPromoCode;
            }
        },
        lessThanMinimum(i) {
            return i < 0;
        },
        changePlanSize(event) {
            this.quantity = parseInt(event.target.value);
            this.calculateTotal();
        },
        checkPromoCode() {
            if ( !this.userPromoCode.length ) {
                return;
            }

            if( this.userPromoCode === 'MSCALUM' && this.plan_identifier !== this.currentTier3.identifier) {
                this.error = 'The MSCALUM promo code must be used on the Enterprise Tier';
                return
            }

            for ( const promo of this.promoCodes ) {
                if (promo['code'] == this.userPromoCode.toUpperCase()) {
                    this.discount.code = promo['code'];
                    this.discount.duration = promo['coupon']['duration'];
                    this.discount.duration_in_months = promo['coupon']['duration_in_months'];
                    this.discount.amount_off = promo['coupon']['amount_off'];
                    this.discount.percent_off = promo['coupon']['percent_off'];
                    this.error = '';
                    this.calculateTotal();
                    return
                }
            }
            this.error = 'Promo code: '+this.userPromoCode+' not found'
        },
        moneyToString( num ) {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });

            return formatter.format(parseFloat(num));
        },
        toggleCurrentPlan() {
            if ( this.currentPlans === 'yearly' ) {
                this.billingInterval = 'month';
                this.currentPlans = 'monthly'

            } else {
                this.currentPlans = 'yearly'
                this.billingInterval = 'year';
            }

            switch(this.plan_identifier){
                case this.currentTier1.identifier:
                    this.plan = this.currentTier1;
                    break
                case this.currentTier2.identifier:
                    this.plan = this.currentTier2;
                    break
                case this.currentTier3.identifier:
                    this.plan = this.currentTier3;
                    break
            };

            this.discount = {
                code: '',
                duration: '',
                duration_in_months: '',
                percent_off: 0,
                amount_off: 0,
            };
            this.totalDiscount = 0;
            this.error = '';

            return this.calculateTotal();
        },
        planChecked(value) {
            if (this.plan === value) {
                return true;
            }
            return false;
        },
        currentPlanPrice() {
            return this.moneyToString(this.plan.price/ 100);
        },
        showPlanPrice(price) {
            return this.moneyToString(price/ 100);
        },
        changePlan(event) {
             switch(this.plan_identifier){
                case this.currentTier1.identifier:
                    this.plan = this.currentTier1;
                    break
                case this.currentTier2.identifier:
                    this.plan = this.currentTier2;
                    break
                case this.currentTier3.identifier:
                    this.plan = this.currentTier3;
                    break
            };

            this.discount = {
                code: '',
                duration: '',
                duration_in_months: '',
                percent_off: 0,
                amount_off: 0,
            };
            this.totalDiscount = 0;
            this.error = '';

            this.calculateTotal();
        },
    }
}
</script>
